import React, { useRef, forwardRef, useImperativeHandle } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import CN from "classnames";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onImageUpload?: (
    formData: FormData
  ) => Promise<{ imageUrl: { newFilePath: string } }>;
}

export interface RichTextEditorRef {
  getEditor: () => any;
}

const RichTextEditor = React.memo(
  forwardRef<RichTextEditorRef, RichTextEditorProps>(
    ({ value, onChange, placeholder, className, onImageUpload }, ref) => {
      const quillRef = useRef<ReactQuill | null>(null);

      useImperativeHandle(ref, () => ({
        getEditor: () => quillRef.current?.getEditor(),
      }));

      const imageHandler = async () => {
        if (!onImageUpload) return;

        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute(
          "accept",
          "image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/tiff"
        );
        input.click();

        input.onchange = async () => {
          const files = input.files;
          if (!files || files.length === 0) return;
          const file = files[0];

          const formData = new FormData();
          formData.append("image", file);

          try {
            const res = await onImageUpload(formData);
            if (!quillRef.current) return;
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection();
            editor.insertEmbed(
              range?.index || 0,
              "image",
              res.imageUrl.newFilePath
            );
          } catch (err) {
            console.error("Upload image error:", err);
          }
        };
      };

      const modules = {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
          ],
          handlers: {
            image: imageHandler,
          },
        },
      };

      return (
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          placeholder={placeholder}
          className={CN("block w-full p-3 border rounded-md", className)}
        />
      );
    }
  )
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
